import { clamp } from '../../utils/Math';

/**
 * This contains logic related to converting client coordinates into positions in the SliderHandleContainer and to
 * positioning handles.  The SliderHandleContainer needs this when processing mouse and touch events.
 */
export function convertClientCoordinateIntoProportionOfContainer(
  // Client coordinates means, basically, the location on the page irrespective of the current viewport position or
  // zoom level.  E.g. 0,0 is always the top left of the page.
  xPositionInClientCoordinates: number,
  containerBoundingClientRect: ClientRect
): number {
  const xPositionRelativeToLeftOfSlider = xPositionInClientCoordinates - containerBoundingClientRect.left;
  const proportion = xPositionRelativeToLeftOfSlider / (Math.max(1, containerBoundingClientRect.width));
  return clamp(proportion, 0, 1);
}

/**
 * This function handles calculation of the new position for a single handle being dragged when a move
 * event occurs.
 */
export function calculateProportionalPositionWhenDraggingSingleHandle(
  sourceXPositionForDragInClientCoordinates: number,
  sourceHandlePositionAsProportion: number,
  newXPositionInClientCoordinates: number,
  containerBoundingClientRect: ClientRect
): number {
  const difference = newXPositionInClientCoordinates - sourceXPositionForDragInClientCoordinates;
  const proportionalDifference = difference / containerBoundingClientRect.width;

  const newHandlePosition = sourceHandlePositionAsProportion + proportionalDifference;
  return clamp(newHandlePosition, 0, 1);
}

/**
 * This function basically does all the math required to calculate what the new position for both handles should be
 * when they are both being dragged and a move event occurs.  This is more complicated than dragging a single handle
 * because in that case, we can (more or less) just move the handle directly underneath the move event's location.
 */
export function calculateProportionalPositionsWhenDraggingBothHandles(
  // We need these source positions to determine how far we should move the handles
  sourceXPositionForDragInClientCoordinates: number,
  sourceLeftHandlePositionAsProprtion: number,
  sourceRightHandlePositionAsProprtion: number,
  newXPositionInClientCoordinates: number,
  containerBoundingClientRect: ClientRect
): { leftHandlePosition: number, rightHandlePosition: number } {

  const difference = newXPositionInClientCoordinates - sourceXPositionForDragInClientCoordinates;
  const proportionalDifference = difference / containerBoundingClientRect.width;

  const newLeftHandlePosition = sourceLeftHandlePositionAsProprtion + proportionalDifference;
  const newRightHandlePosition = sourceRightHandlePositionAsProprtion + proportionalDifference;

  const sourceProportionalDifferenceBetweenHandles =
    sourceRightHandlePositionAsProprtion - sourceLeftHandlePositionAsProprtion;

  if (newLeftHandlePosition < 0) {
    return {
      leftHandlePosition: 0,
      rightHandlePosition: sourceProportionalDifferenceBetweenHandles,
    };
  } else if (newRightHandlePosition > 1) {
    return {
      leftHandlePosition: 1 - sourceProportionalDifferenceBetweenHandles,
      rightHandlePosition: 1,
    };
  } else {
    return {
      leftHandlePosition: newLeftHandlePosition,
      rightHandlePosition: newRightHandlePosition,
    };
  }
}
